/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import isNull from 'lodash.isnull';
import isString from 'lodash.isstring';
import {translate} from '@ion/core/src/locale/locale';
import Dropdown from '../components/menus/Dropdown';

export type TimezoneChooserProps = {
  label?: string;
  readOnly?: boolean;
  onLoad?: (arg: boolean) => void;
  onChange?: (value: {name: string, value: string, required: boolean}) => void;
  defaultValue?: string;
  required?: boolean;
  hasError?: boolean;
  indicateDefault?: boolean;
  name?: string;
  placeholder?: string;
  includeNone?: boolean;
  noneOptionValue?: any;
  noneOptionLabel?: string;
  errorText?: string;
}

type TimeZoneOption = {
  value: string,
  label: string
};

export const getLabel = (val: string): string => {
  const timezones = translate('timezones:list', {returnObjects: true});
  return timezones?.find(({value}: {value: string, label: string}) => {
    return value === val;
  })?.label;
};

/**
 * Updates the list of timezones by appending the browser default indicator to the label
 * of the timezone which matches the user's browser default timezone.
 *
 * @param timezones The list of timezones to search and update.
 */
const includeDefaultIndicator = (timezones: TimeZoneOption[]): TimeZoneOption[] => {
  if (Intl?.DateTimeFormat) {
    const defaults = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
    if (defaults) {
      return timezones.map((option: TimeZoneOption) => {
        if (option.value === defaults) {
          option.label = `${option.label} (${translate('timezones:fields.default')})`;
        }
        return option;
      });
    }
  }
  return timezones;
};

/**
 * @TimezoneChooser
 *
 * The TimezoneChooser provides a list of timezones that are stored
 * in the timezones.json translations file. The values in each object
 * are the CTC compatible values. OnChange prop passes back the label
 * and value from the selected option label.
 *
 * ```typescript
 *
 *    import {TimezoneChooser} from '@ion/dls/src/editors/';
 *
 *    <TimezoneChooser required />
 *
 *    // read-only mode ...
 *    <TimezoneChooser readOnly
 *      defaultValue='America/Los_Angeles' />
 *
 *    // no default value with basic callback subscriptions ...
 *    <TimezoneChooser required
 *      onChange={handleTimezoneChange}
 *      name='timezone' />
 *
 *    // no default value with browser default indicator disabled ...
 *    <TimezoneChooser indicateDefault
 *      onChange={handleTimezoneChange}
 *      name='timezone' />
 *
 *    // provide a default value and error handling ...
 *    <TimezoneChooser required
 *      name='timezone'
 *      hasError={true}
 *      onChange={handleTimezoneChange}
 *      defaultValue='America/Los_Angeles' />
 *
 * ```
 *
 * @param {Function} onChange: Callback prop which will be invoked with Timezone Properties
 *   inherited properties.
 * @param {string} defaultValue: The CTC string value for the timezone
 * @param {boolean} required: Determines if the dropdown should be marked as required and
 *   teh asterisk shown on the fieldLabel. Defaults to false.
 * @param {boolean} hasError: Passthrough prop sent to the Dropdown that will show a red box
 *   around the dropdown if true. Defaults to false.
 * @param {boolean} readOnly: When true, the component will be displayed in read-only mode;
 *   that is, disabled. Defaults to false.
 * @param {string} name: for the component that gets passed to the root element.
 * @param {string} placeholder: overrides the default placeholder for the field.
 * @param {boolean} includeNone: Indicates whether to include a 'none' option. Defaults to
 *   false.
 * @param {any} noneOptionValue: If a 'none' option is to be included, the option value.
 *   Defaults to null.
 * @param {string} noneOptionLabel: If a 'none' option is to be included, the option label.
 *   Defaults to 'None'.
 */
export const TimezoneChooser = ({
  onChange,
  defaultValue,
  name,
  errorText,
  required = false,
  hasError = false,
  readOnly = false,
  indicateDefault = false,
  label = translate('common:editors.timezone.label'),
  placeholder = translate('common:editors.timezone.placeholder'),
  includeNone = false,
  noneOptionValue = null,
  noneOptionLabel = translate('common:options.none')
}: TimezoneChooserProps) => {
  const [value, setValue] = useState('');
  const timezoneOptions   = translate('timezones:list', {returnObjects: true});

  useEffect(() => {
    if (isString(defaultValue)|| isNull(defaultValue)) {
      const matchingOption = timezoneOptions.find(option => option.value === defaultValue);
      setValue(matchingOption?.label);
      onChange?.({
        value : matchingOption,
        name,
        required
      });
    }
  }, [defaultValue]);

  function handleChange({target}) {
    const matchingOption = timezoneOptions.find(option => option.label === target.value);
    setValue(matchingOption.label);
    onChange?.({
      name  : target.name,
      value : matchingOption,
      required
    });
  }

  return (
    <Dropdown displaySelectedLabel
      options={indicateDefault ? includeDefaultIndicator(timezoneOptions) : timezoneOptions}
      includeNone={includeNone}
      noneOptionValue={noneOptionValue}
      noneOptionLabel={noneOptionLabel}
      required={required}
      hasError={hasError}
      errorText={errorText}
      placeholder={placeholder}
      disabled={readOnly}
      fieldLabel={label}
      onChange={handleChange}
      value={value}
      name={name} />
  );
};

export default TimezoneChooser;
